import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Search, Gift, Sparkles, UserPlus, ArrowLeft, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QRCodeCanvas } from "qrcode.react";
import { api, STAMPS_PER_REWARD } from "@/lib/api";
import { PunchCard } from "@/components/PunchCard";
import { InstallAppButton } from "@/components/InstallAppButton";

const HERO = "https://images.pexels.com/photos/19510302/pexels-photo-19510302.jpeg";

export default function CustomerPage() {
  const [phone, setPhone] = useState("");
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [regName, setRegName] = useState("");

  const lookup = async (e) => {
    e?.preventDefault();
    if (!phone.trim()) return;
    setLoading(true);
    setNotFound(false);
    try {
      const c = await api.getByPhone(phone.trim());
      setCustomer(c);
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
        setCustomer(null);
      } else {
        toast.error("Errore durante la ricerca");
      }
    } finally {
      setLoading(false);
    }
  };

  const register = async (e) => {
    e.preventDefault();
    if (!regName.trim()) return;
    setLoading(true);
    try {
      const c = await api.createCustomer(regName.trim(), phone.trim());
      setCustomer(c);
      setNotFound(false);
      toast.success("Tessera creata! Benvenuto/a " + c.name);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella creazione");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setCustomer(null);
    setNotFound(false);
    setPhone("");
    setRegName("");
  };

  return (
    <div className="relative z-[2] min-h-[calc(100vh-68px)]">
      <AnimatePresence mode="wait">
        {!customer ? (
          <motion.div
            key="lookup"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid lg:grid-cols-2 min-h-[calc(100vh-68px)]"
          >
            {/* Left hero */}
            <div className="relative hidden lg:block overflow-hidden">
              <img src={HERO} alt="Bar" className="absolute inset-0 w-full h-full object-cover opacity-60" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
              <div className="relative z-10 flex flex-col justify-end h-full p-12">
                <p className="font-mono text-xs tracking-[0.3em] text-primary mb-4">PROGRAMMA FEDELTÀ</p>
                <h1 className="font-serif text-5xl xl:text-6xl leading-[0.95] mb-4">
                  Ogni {STAMPS_PER_REWARD} caffè,<br />uno è <span className="text-primary italic">offerto da noi</span>.
                </h1>
                <p className="text-muted-foreground max-w-md text-lg">
                  Colleziona i tuoi timbri ad ogni acquisto e sblocca la tua prossima consumazione gratuita.
                </p>
              </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-6 sm:p-12">
              <div className="w-full max-w-md">
                <div className="lg:hidden mb-8">
                  <p className="font-mono text-xs tracking-[0.3em] text-primary mb-3">PROGRAMMA FEDELTÀ</p>
                  <h1 className="font-serif text-4xl leading-tight">
                    Ogni {STAMPS_PER_REWARD} caffè, uno è <span className="text-primary italic">offerto</span>.
                  </h1>
                </div>

                <div className="rounded-2xl bg-black/40 backdrop-blur-xl ring-1 ring-white/10 p-7 sm:p-8 shadow-[0_8px_32px_rgba(0,0,0,0.6)]">
                  {!notFound ? (
                    <>
                      <h2 className="font-serif text-2xl mb-1">La tua tessera</h2>
                      <p className="text-sm text-muted-foreground mb-6">
                        Inserisci il tuo numero di telefono per vedere i tuoi timbri.
                      </p>
                      <form onSubmit={lookup} className="space-y-4">
                        <Input
                          data-testid="phone-input"
                          type="tel"
                          inputMode="tel"
                          placeholder="Es. 333 1234567"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="h-12 bg-zinc-900 border-zinc-800 text-base focus-visible:ring-primary"
                        />
                        <Button
                          data-testid="lookup-btn"
                          type="submit"
                          disabled={loading || !phone.trim()}
                          className="w-full h-12 rounded-full bg-primary hover:bg-amber-500 text-primary-foreground font-semibold transition-colors duration-300"
                        >
                          <Search className="w-4 h-4 mr-2" />
                          {loading ? "Ricerca..." : "Trova la mia tessera"}
                        </Button>
                      </form>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <UserPlus className="w-5 h-5 text-primary" />
                        <h2 className="font-serif text-2xl">Nuovo cliente</h2>
                      </div>
                      <p className="text-sm text-muted-foreground mb-6">
                        Nessuna tessera per <span className="text-white">{phone}</span>. Creane una nuova!
                      </p>
                      <form onSubmit={register} className="space-y-4">
                        <Input
                          data-testid="register-name-input"
                          placeholder="Il tuo nome"
                          value={regName}
                          onChange={(e) => setRegName(e.target.value)}
                          className="h-12 bg-zinc-900 border-zinc-800 text-base focus-visible:ring-primary"
                        />
                        <Button
                          data-testid="register-btn"
                          type="submit"
                          disabled={loading || !regName.trim()}
                          className="w-full h-12 rounded-full bg-primary hover:bg-amber-500 text-primary-foreground font-semibold transition-colors duration-300"
                        >
                          Crea la mia tessera
                        </Button>
                        <button
                          type="button"
                          onClick={() => setNotFound(false)}
                          data-testid="back-to-lookup"
                          className="w-full text-sm text-muted-foreground hover:text-white transition-colors"
                        >
                          Torna indietro
                        </button>
                      </form>
                    </>
                  )}
                </div>
                <div className="mt-4 flex justify-center">
                  <InstallAppButton />
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto px-5 sm:px-6 py-10 sm:py-16"
          >
            <div className="flex items-center justify-between mb-8">
              <button
                onClick={reset}
                data-testid="lookup-again-btn"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Cerca un'altra tessera
              </button>
              <InstallAppButton />
            </div>

            <div className="rounded-3xl bg-black/40 backdrop-blur-xl ring-1 ring-white/10 p-7 sm:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.6)]">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-primary/80 mb-1">TESSERA FEDELTÀ</p>
                  <h2 className="font-serif text-3xl sm:text-4xl" data-testid="customer-name">{customer.name}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{customer.phone}</p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-4xl text-primary" data-testid="customer-stamps">{customer.stamps}<span className="text-xl text-muted-foreground">/{STAMPS_PER_REWARD}</span></p>
                  <p className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase">Timbri</p>
                </div>
              </div>

              <PunchCard stamps={customer.stamps} size="lg" />

              <div className="mt-8 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {STAMPS_PER_REWARD - customer.stamps} timbri al prossimo premio
                </span>
                <span className="text-muted-foreground">Totale acquisti: <span className="text-white">{customer.total_stamps}</span></span>
              </div>
            </div>

            {/* QR CODE PERSONALE */}
            <div className="mt-6 rounded-2xl bg-zinc-900/60 ring-1 ring-white/10 p-6 flex flex-col sm:flex-row items-center gap-6" data-testid="customer-qr">
              <div className="bg-white p-3 rounded-xl shrink-0">
                <QRCodeCanvas
                  value={`MIRO:${customer.id}`}
                  size={140}
                  level="M"
                  fgColor="#0a0a0a"
                  bgColor="#ffffff"
                  data-testid="qr-canvas"
                />
              </div>
              <div className="text-center sm:text-left">
                <div className="flex items-center gap-2 justify-center sm:justify-start mb-2">
                  <QrCode className="w-4 h-4 text-primary" />
                  <p className="font-mono text-xs tracking-[0.2em] text-primary">IL TUO QR</p>
                </div>
                <h3 className="font-serif text-2xl mb-1">Mostralo alla cassa</h3>
                <p className="text-sm text-muted-foreground">
                  Fai scansionare questo codice al barista per aggiungere il timbro in un secondo.
                  Salva o fai uno screenshot per averlo sempre con te.
                </p>
              </div>
            </div>

            {customer.rewards_available > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="reward-banner"
                className="mt-6 rounded-2xl bg-gradient-to-r from-primary/20 to-amber-500/10 ring-1 ring-primary/40 p-6 flex items-center gap-4"
              >
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/25 ring-1 ring-primary/50">
                  <Gift className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <h3 className="font-serif text-2xl">Premio Sbloccato!</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Hai <span className="text-primary font-semibold">{customer.rewards_available}</span> consumazione/i gratuita/e.
                    Mostralo alla cassa per riscattarlo.
                  </p>
                </div>
              </motion.div>
            )}

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="rounded-xl bg-zinc-900/60 ring-1 ring-white/5 p-4 text-center">
                <p className="font-serif text-3xl text-primary" data-testid="rewards-available">{customer.rewards_available}</p>
                <p className="text-xs text-muted-foreground mt-1">Premi disponibili</p>
              </div>
              <div className="rounded-xl bg-zinc-900/60 ring-1 ring-white/5 p-4 text-center">
                <p className="font-serif text-3xl">{customer.rewards_redeemed}</p>
                <p className="text-xs text-muted-foreground mt-1">Premi riscattati</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
